import { Injectable } from '@angular/core';
import { Store, StoreConfig } from '@datorama/akita';
import { findUniqueByControlId } from '../../../shared/utils';
import { createInitialSessionState, SessionModel } from './session.models';
import { clearSession } from './session-storage.service';
import { InvalidControlModel, ValidationFailureModel } from '../../../cmg-1003/models/validation-panel.model';
import { Cmg1003SectionTypes } from '../../../cmg-1003/models/section-types.enum';
import { Cmg1003SubSectionTypes } from '../../../cmg-1003/models/sub-section-types.enum';
import { Subject } from 'rxjs';
import { AssetsSubTypes, Cmg1003Model } from '../../../cmg-1003/models';
import { iStoreActions } from '../../../loan-state/core/store-actions.interface';

/**
 * Basic Akita session store
 * TODO: Store the session in local storage and read from there if needed.
 */
@Injectable({
  providedIn: 'root',
})
@StoreConfig({ name: 'session' })
export class SessionStore extends Store<SessionModel> implements iStoreActions<Cmg1003Model> {
  constructor() {
    super(createInitialSessionState());
  }

  preInitialize(all1003Model: Cmg1003Model): void {
    this.update({
      loanStatusId: all1003Model?.loanStatusTypeId,
      rateLockStatus: all1003Model?.rateLockStatus,
      borrowerHasServedInArmedForces: null,
    });
    this.update({
      validationErrors: [],
      validationWarnings: [],
    });
  }

  private _updateValidationFailures(
    existingStoreValidations: ValidationFailureModel[],
    currentSectionValidations: InvalidControlModel[],
    section: Cmg1003SectionTypes,
    subSection?: Cmg1003SubSectionTypes,
  ) {
    const otherInvalidSections = existingStoreValidations.filter((t) => !(t.section === section && t.subSection === subSection));

    // [Scenario]: User fixed current section. Return any remaining invalid sections, excluding current section.
    if (!currentSectionValidations || currentSectionValidations.length === 0) {
      return otherInvalidSections;
    }

    let existingStoreInvalidSection = existingStoreValidations.find((t) => t.section === section && t.subSection === subSection);

    // [Scenario]: Multiple child component form control validations with the same section,
    // OR the primary and additional borrower have validations on the same controls.
    if (existingStoreInvalidSection) {
      // Combine invalid controls for the same section.
      // Will filter out duplicates below using findUniqueByControlId.
      existingStoreInvalidSection.invalidControls = [...existingStoreInvalidSection.invalidControls, ...currentSectionValidations];
    }
    // [Scenario]: A valid section became invalid.
    else {
      existingStoreInvalidSection = { section: section, subSection: subSection, invalidControls: currentSectionValidations };
    }

    // [Scenario]: Sections can accumulate duplicate warnings/errors, since multiple controls share the same warning/error message.
    // For display purposes, duplicates must be removed.
    existingStoreInvalidSection.invalidControls = findUniqueByControlId(existingStoreInvalidSection.invalidControls);

    return [...otherInvalidSections, existingStoreInvalidSection];
  }

  clearAllValidationErrors() {
    this.update(() => {
      return {
        validationErrors: [],
        validationWarnings: [],
      };
    });
  }

  clearSectionValidationErrors(sectionToClear: Cmg1003SectionTypes) {
    this.update((state) => {
      return {
        validationErrors: state.validationErrors.filter((error) => error.section !== sectionToClear),
      };
    });
  }

  clearSectionValidationWarnings(sectionToClear: Cmg1003SectionTypes, subSectionToClear?: Cmg1003SubSectionTypes) {
    this.update((state) => {
      return {
        validationWarnings: state.validationWarnings.filter((error) => !(error.section === sectionToClear && error.subSection === subSectionToClear)),
      };
    });
  }

  clearSectionValidationWarningsByControlId(sectionToClear: Cmg1003SectionTypes, controlIdsToClear: string[]) {
    this.update((state) => {
      const warnings = state.validationWarnings;
      const section = warnings?.find((s) => s.section === sectionToClear);
      if (section) {
        section.invalidControls = section.invalidControls.filter((c) => !controlIdsToClear.includes(c.controlId));
        warnings[warnings.findIndex((w) => w.section === sectionToClear)] = section;
      }
      return {
        validationWarnings: warnings,
      };
    });
  }

  login(name: string, token: string) {
    this.update({ name: name, token: token });
  }

  logout() {
    clearSession();
    this.update(createInitialSessionState());
  }

  /**
   *
   * @param section The 1003 section to validate.
   * @param invalidControls The invalid controls of the targeted 1003 section.
   */
  setValidationError(section: Cmg1003SectionTypes, invalidControls: InvalidControlModel[], subSection?: Cmg1003SubSectionTypes) {
    this.update((state) => {
      const validations = {
        validationErrors: this._updateValidationFailures(
          state.validationErrors,
          invalidControls?.filter((ic) => !!ic.invalidMessage),
          section,
          subSection,
        ),
        validationWarnings: this._updateValidationFailures(
          state.validationWarnings,
          invalidControls?.filter((ic) => !!ic.invalidMessage),
          section,
          subSection,
        ),
      };

      return validations;
    });
  }

  updateNavPosition(navPosition: Cmg1003SectionTypes) {
    if (this.getValue().scrollAdjustNavItem !== navPosition) {
      this.update({ scrollAdjustNavItem: navPosition });
    }
  }

  updateLastRefreshDate(): void {
    this.update({ lastRefreshDateTime: new Date() });
  }

  /**
   * Used for validations when needing to go to the Asset Editor and select a specific type of Asset
   */
  navigateToAssetEditor$ = new Subject<AssetsSubTypes>();

  triggerRevalidation() {
    this.update({
      revalidateValidationTrigger: new Date(),
    });
  }
}
